import {useEffect, useState} from 'react';

import {usePrevious} from '@/hooks/usePrevious';
import {TrackPlayer} from '@/services/trackPlayer';
import {setRemoteHandler} from '@/services/trackPlayer/mediaSession';
import {ITrack} from '@/types/common';

export const useEmbedPlayer = (tracks: ITrack[]) => {
  const [index, setIndex] = useState(0);
  const currentTrack = tracks[index];
  const prevCurrentTrack = usePrevious(currentTrack);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);

  const hasPrevious = index > 0;
  const hasNext = index >= 0 && index < tracks.length - 1;

  const play = () => {
    setIsPlaying(true);
    TrackPlayer.play();
  };

  const pause = () => {
    setIsPlaying(false);
    TrackPlayer.pause();
  };

  const togglePlay = () => {
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  };

  const playTrack = (track: ITrack) => {
    setIndex(
      Math.max(
        tracks.findIndex(({id}) => id === track.id),
        0,
      ),
    );
  };

  const next = () => {
    if (hasNext) {
      setIndex(i => i + 1);
    }
  };

  const previous = async () => {
    const position = await TrackPlayer.getPosition();

    if (hasPrevious && position < 3) {
      setIndex(i => i - 1);
    } else {
      await TrackPlayer.seekTo(0);
      play();
    }
  };

  const onEnded = async () => {
    if (hasNext) {
      next();
    } else if (index > 0) {
      setIndex(0);
    } else {
      await TrackPlayer.seekTo(0);
      play();
    }
  };

  useEffect(() => {
    if (currentTrack) {
      TrackPlayer.add(currentTrack);
      TrackPlayer.seekTo(0);

      if (prevCurrentTrack) {
        play();
      }
    }
  }, [currentTrack?.id]);

  useEffect(() => {
    if (tracks.length > 0) {
      setIndex(0);
    }
  }, [tracks]);

  useEffect(() => {
    if (tracks.length > 0) {
      const onEndedListener = () => onEnded();
      const onWaitingListener = () => setIsBuffering(true);
      const onPlayingListener = () => setIsBuffering(false);

      TrackPlayer.addEventListener('ended', onEndedListener);
      TrackPlayer.addEventListener('waiting', onWaitingListener);
      TrackPlayer.addEventListener('playing', onPlayingListener);

      setRemoteHandler('play', togglePlay);
      setRemoteHandler('pause', togglePlay);
      setRemoteHandler('stop', () => {
        TrackPlayer.reset();
        pause();
      });
      setRemoteHandler('previoustrack', previous);
      setRemoteHandler('nexttrack', next);
      setRemoteHandler('seekto', ({seekTime}) => {
        if (typeof seekTime === 'number') {
          TrackPlayer.seekTo(seekTime);
        }
      });

      return () => {
        TrackPlayer.removeEventListener?.('ended', onEndedListener);
        TrackPlayer.removeEventListener?.('waiting', onWaitingListener);
        TrackPlayer.removeEventListener?.('playing', onPlayingListener);
      };
    }
  }, [tracks, index, isPlaying]);

  return {
    currentTrack,
    isPlaying,
    isBuffering,
    hasPrevious,
    hasNext,
    play,
    pause,
    togglePlay,
    playTrack,
    next,
    previous,
    seekTo: TrackPlayer.seekTo,
  };
};
